import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'

const { logger } = Sentry;
import './Hero.scss'

function Hero() {
  const { loginWithPopup, isAuthenticated } = useAuth0()
  const [isLoading, setIsLoading] = useState(false)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)
  const [hasVideoEnded, setHasVideoEnded] = useState(false)
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard')
    }
  }, [isAuthenticated, navigate])

  const handleGetStarted = async () => {
    setIsLoading(true)
    try {
      const width = 700
      const height = 900
      const left = window.screen.width / 2 - width / 2
      const top = window.screen.height / 2 - height / 2
      
      const popup = window.open(
        '',
        'auth0:authorize:popup',
        `width=${width},height=${height},left=${left},top=${top},resizable,scrollbars=yes,status=1`
      )
      
      await loginWithPopup({
        authorizationParams: {
          screen_hint: 'signup'
        }
      }, { popup } as any)
      // Navigation will happen via useEffect when isAuthenticated becomes true
    } catch (error) {
      logger.error('Login error', {
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsLoading(false)
    }
  }

  const handleVideoClick = () => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause()
        setIsVideoPlaying(false)
      } else {
        videoRef.current.play()
        setIsVideoPlaying(true)
        setHasVideoEnded(false)
      }
    }
  }

  const handleReplay = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0
      videoRef.current.play()
      setIsVideoPlaying(true)
      setHasVideoEnded(false)
    }
  }

  return (
    <section className="py-5">
      <div className="container">
        <div className="row align-items-center g-4">
          <div className="col-lg-6">
            <h1 className="display-4 fw-bold mb-4 lh-sm" style={{ 
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
              letterSpacing: '-0.02em'
            }}>
              Land More Interviews with{' '}
              <span className="text-primary">AI-Powered Job Applications</span>
            </h1>
            <p className="lead text-muted mb-4" style={{
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
              fontSize: '20px',
              lineHeight: '28px',
              fontWeight: '500'
            }}>
              JobsReadyAI helps you get tailored resumes, interview prep, and application tracking — everything you need to stand out and get hired faster.
            </p>
            <div className="mb-4">
              <div className="d-flex align-items-center gap-3 mb-2">
                <span className="text-success fw-bold">✓</span>
                <span style={{ fontSize: '16px', color: '#606064' }}>Free forever — 3 jobs included</span>
              </div>
              <div className="d-flex align-items-center gap-3">
                <span className="text-success fw-bold">✓</span>
                <span style={{ fontSize: '16px', color: '#606064' }}>No credit card required</span>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-primary btn-lg text-uppercase fw-bold"
              aria-label="Get started with JobsReadyAI for free"
              onClick={handleGetStarted}
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : 'Get started now'}
            </button>
          </div>
          <div className="col-lg-6 text-center">
            <div className="video-container">
              <video 
                ref={videoRef}
                src="/videos/landing.mp4" 
                poster="/images/landing.png"
                className="img-fluid hero-video"
                onClick={handleVideoClick}
                onPlay={() => setIsVideoPlaying(true)}
                onPause={() => setIsVideoPlaying(false)}
                onEnded={() => {
                  setIsVideoPlaying(false)
                  setHasVideoEnded(true)
                }}
                playsInline
              />
              {hasVideoEnded ? (
                <div className="play-button-overlay replay-overlay" onClick={handleReplay}>
                  <svg 
                    width="80" 
                    height="80" 
                    viewBox="0 0 80 80" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="40" cy="40" r="40" fill="rgba(0, 0, 0, 0.6)"/>
                    <path 
                      d="M32 24L32 56L56 40L32 24Z" 
                      fill="white"
                    />
                  </svg>
                </div>
              ) : !isVideoPlaying && (
                <div className="play-button-overlay" onClick={handleVideoClick}>
                  <svg 
                    width="80" 
                    height="80" 
                    viewBox="0 0 80 80" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="40" cy="40" r="40" fill="rgba(0, 0, 0, 0.6)"/>
                    <path 
                      d="M32 24L32 56L56 40L32 24Z" 
                      fill="white"
                    />
                  </svg>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Hero
